import { ByteQueue } from "./bytes.js";
import { LscError } from "./errors.js";
import { decryptFrame, encryptFrame } from "./frame.js";
import {
  buildHeader,
  chunkSizeOf,
  encryptedSizeOf,
  frameCount,
  frameLength,
  frameOffset,
  parseHeader,
  type Header,
} from "./header.js";
import { toAesKey } from "./keys.js";

export interface EncryptStreamOptions {
  /** K_file — raw 32 bytes or an imported AES-GCM CryptoKey with "encrypt" usage */
  key: CryptoKey | Uint8Array;
  /** L — exact plaintext length of the WHOLE file; the stream fails if input length differs from what it expects */
  plaintextLength: number;
  chunkLog2?: number;
  /** 7 bytes; random if omitted. Reuse ONLY when resuming the same container. */
  noncePrefix?: Uint8Array;
  /**
   * Resume support (§12): start emitting at this frame. The input must then be the plaintext starting at
   * byte startFrameIndex × chunkSize (e.g. file.slice(k * C).stream()). The header is emitted only for frame 0.
   */
  startFrameIndex?: number;
  /** called after each frame with cumulative plaintext bytes processed (counted from the start of the file) */
  onProgress?: (plaintextBytesDone: number) => void;
}

export interface EncryptStreamHandle {
  stream: TransformStream<Uint8Array, Uint8Array>;
  /** the 32-byte header (emitted as the first output chunk when starting from frame 0) */
  header: Uint8Array;
  parsedHeader: Header;
  /** E — exact container length of the whole file */
  encryptedSize: number;
  frameCount: number;
  /** container byte offset of the first emitted byte (0 when starting from frame 0) */
  outputOffset: number;
  /** plaintext bytes the stream expects to receive */
  expectedInputLength: number;
}

/**
 * §5 / §8 — plaintext bytes → LSC1 container bytes.
 * Memory: O(chunkSize). Output is header, then frames in order.
 */
export function createEncryptStream(o: EncryptStreamOptions): EncryptStreamHandle {
  const header = buildHeader({
    plaintextLength: o.plaintextLength,
    ...(o.chunkLog2 !== undefined ? { chunkLog2: o.chunkLog2 } : {}),
    ...(o.noncePrefix !== undefined ? { noncePrefix: o.noncePrefix } : {}),
  });
  const h = parseHeader(header);
  const C = chunkSizeOf(h);
  const n = frameCount(h);
  const L = h.plaintextLength;
  const start = o.startFrameIndex ?? 0;
  if (!Number.isInteger(start) || start < 0 || start >= n) {
    throw new LscError("BAD_INPUT", `startFrameIndex ${start} out of range 0..${n - 1}`);
  }
  const expectedInputLength = L - start * C;
  const outputOffset = start === 0 ? 0 : frameOffset(h, start);

  let key: CryptoKey;
  const q = new ByteQueue();
  let index = start;
  let received = 0;
  let done = start * C;

  const stream = new TransformStream<Uint8Array, Uint8Array>({
    async start(ctrl) {
      key = await toAesKey(o.key, ["encrypt"]);
      if (start === 0) ctrl.enqueue(header);
    },
    async transform(chunk, ctrl) {
      if (!(chunk instanceof Uint8Array)) throw new LscError("BAD_INPUT", "encrypt stream expects Uint8Array");
      received += chunk.length;
      if (received > expectedInputLength) throw new LscError("INPUT_OVERFLOW", `got more than ${expectedInputLength} bytes`);
      q.push(chunk);
      while (q.length >= C && index < n) {
        const isLast = index === n - 1;
        const pt = q.take(C);
        ctrl.enqueue(await encryptFrame(key, header, index, isLast, pt));
        index++;
        done += pt.length;
        o.onProgress?.(done);
      }
    },
    async flush(ctrl) {
      if (received !== expectedInputLength) {
        throw new LscError("INPUT_UNDERFLOW", `got ${received} of ${expectedInputLength} bytes`);
      }
      if (index < n) {
        // exactly the final (possibly empty) frame remains
        const pt = q.take(q.length);
        ctrl.enqueue(await encryptFrame(key, header, index, true, pt));
        index++;
        done += pt.length;
        o.onProgress?.(done);
      }
    },
  });

  return {
    stream,
    header,
    parsedHeader: h,
    encryptedSize: encryptedSizeOf(h),
    frameCount: n,
    outputOffset,
    expectedInputLength,
  };
}

export interface DecryptStreamOptions {
  key: CryptoKey | Uint8Array;
  /** E as known from the manifest/HEAD; if provided, the header-derived size must match (§5.5 step 2) */
  expectedEncryptedSize?: number;
  onHeader?: (header: Header, raw: Uint8Array) => void;
  onProgress?: (plaintextBytesDone: number) => void;
}

/**
 * §5.5 / §9 — LSC1 container bytes → plaintext bytes with full verification.
 * Emits nothing for a frame until its tag verifies. Fails on truncation, reordering, trailing data.
 */
export function createDecryptStream(o: DecryptStreamOptions): TransformStream<Uint8Array, Uint8Array> {
  let key: CryptoKey;
  const q = new ByteQueue();
  let header: Uint8Array | null = null;
  let h: Header | null = null;
  let n = 0;
  let E = 0;
  let index = 0;
  let received = 0;
  let done = 0;

  return new TransformStream<Uint8Array, Uint8Array>({
    async start() {
      key = await toAesKey(o.key, ["decrypt"]);
    },
    async transform(chunk, ctrl) {
      if (!(chunk instanceof Uint8Array)) throw new LscError("BAD_INPUT", "decrypt stream expects Uint8Array");
      received += chunk.length;
      q.push(chunk);

      if (!header) {
        if (q.length < 32) return;
        header = q.take(32);
        h = parseHeader(header);
        E = encryptedSizeOf(h);
        n = frameCount(h);
        if (o.expectedEncryptedSize !== undefined && o.expectedEncryptedSize !== E) {
          throw new LscError("SIZE_MISMATCH", `header implies ${E}, expected ${o.expectedEncryptedSize}`);
        }
        o.onHeader?.(h, header);
      }
      if (received > E) throw new LscError("TRAILING_DATA", `received ${received} > ${E}`);

      while (index < n) {
        const fl = frameLength(h!, index);
        if (q.length < fl) break;
        const frame = q.take(fl);
        const pt = await decryptFrame(key, header, index, index === n - 1, frame);
        ctrl.enqueue(pt);
        index++;
        done += pt.length;
        o.onProgress?.(done);
      }
    },
    async flush() {
      if (!header) throw new LscError("BAD_HEADER", "stream ended before a complete header");
      if (index < n) throw new LscError("LENGTH_MISMATCH", `got ${index} of ${n} frames`);
      if (q.length > 0) throw new LscError("TRAILING_DATA");
    },
  });
}

/** Convenience: encrypt a whole in-memory buffer (tests, small payloads). */
export async function encryptBytes(
  key: CryptoKey | Uint8Array,
  plaintext: Uint8Array,
  opts: Omit<EncryptStreamOptions, "key" | "plaintextLength"> = {},
): Promise<Uint8Array> {
  const { stream } = createEncryptStream({ key, plaintextLength: plaintext.length, ...opts });
  return collect(new Blob([plaintext]).stream().pipeThrough(stream));
}

/** Convenience: decrypt a whole in-memory container (tests, small payloads). */
export async function decryptBytes(
  key: CryptoKey | Uint8Array,
  container: Uint8Array,
  opts: Omit<DecryptStreamOptions, "key"> = {},
): Promise<Uint8Array> {
  return collect(new Blob([container]).stream().pipeThrough(createDecryptStream({ key, ...opts })));
}

export async function collect(readable: ReadableStream<Uint8Array>): Promise<Uint8Array> {
  const reader = readable.getReader();
  const parts: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    parts.push(value);
    total += value.length;
  }
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}
